// ============================================================================
// FSA SIGNATURE WORKFLOW SERVICES
// ============================================================================

import { get, post, put, del } from '../../client/axiosClient';
import { buildEndpointWithQuery } from '../../endpoints/base.endpoints';
import {
  fsaAuditFirmEndpoints,
  fsaAuditPartnerEndpoints,
  fsaDirectorSignatoryEndpoints,
  fsaEngagementSignaturePanelEndpoints,
} from '../../endpoints/fsa/fsaSignature.endpoints';
import type { ApiResponse, PaginatedResponse, ID } from '../../types';
import type {
  AuditFirm, CreateAuditFirmRequest, UpdateAuditFirmRequest, AuditFirmSearchRequest,
  AuditPartner, CreateAuditPartnerRequest, UpdateAuditPartnerRequest, AuditPartnerSearchRequest,
  DirectorSignatory, CreateDirectorSignatoryRequest, UpdateDirectorSignatoryRequest, DirectorSignatorySearchRequest,
  EngagementSignaturePanel, UpsertEngagementSignaturePanelRequest,
} from '../../types/fsa/signature.types';

/** Transform backend PagedResult envelope to frontend PaginatedResponse. */
function toPaged<T>(raw: any): PaginatedResponse<T> {
  const paged = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: paged.items ?? [],
    pagination: {
      currentPage: paged.page ?? 1,
      pageSize: paged.pageSize ?? 20,
      totalPages: paged.totalPages ?? 0,
      totalItems: paged.totalCount ?? 0,
      hasNextPage: paged.hasNext ?? false,
      hasPreviousPage: paged.hasPrevious ?? false,
    },
  };
}

// NOTE: pass the RAW path to buildEndpointWithQuery (it prepends /v1
// internally). Passing the already-built endpoint doubles the prefix
// → /v1/v1/FsaAuditFirms 404. See prima-web/CLAUDE.md "doubled prefix".
const FIRMS_BASE       = '/FsaAuditFirms';
const PARTNERS_BASE    = '/FsaAuditPartners';
const DIRECTORS_BASE   = '/FsaDirectorSignatories';

class FsaAuditFirmService {
  async getAll(params?: AuditFirmSearchRequest): Promise<PaginatedResponse<AuditFirm>> {
    const endpoint = params ? buildEndpointWithQuery(FIRMS_BASE, params as any) : fsaAuditFirmEndpoints.list();
    return toPaged<AuditFirm>((await get<any>(endpoint)).data);
  }
  async getById(id: ID): Promise<ApiResponse<AuditFirm>> {
    return (await get<ApiResponse<AuditFirm>>(fsaAuditFirmEndpoints.byId(String(id)))).data;
  }
  async create(body: CreateAuditFirmRequest): Promise<ApiResponse<AuditFirm>> {
    return (await post<ApiResponse<AuditFirm>>(fsaAuditFirmEndpoints.create(), body)).data;
  }
  async update(id: ID, body: UpdateAuditFirmRequest): Promise<ApiResponse<AuditFirm>> {
    return (await put<ApiResponse<AuditFirm>>(fsaAuditFirmEndpoints.update(String(id)), body)).data;
  }
  async remove(id: ID): Promise<void> {
    await del(fsaAuditFirmEndpoints.remove(String(id)));
  }
}

class FsaAuditPartnerService {
  async getAll(params?: AuditPartnerSearchRequest): Promise<PaginatedResponse<AuditPartner>> {
    const endpoint = params ? buildEndpointWithQuery(PARTNERS_BASE, params as any) : fsaAuditPartnerEndpoints.list();
    return toPaged<AuditPartner>((await get<any>(endpoint)).data);
  }
  async getById(id: ID): Promise<ApiResponse<AuditPartner>> {
    return (await get<ApiResponse<AuditPartner>>(fsaAuditPartnerEndpoints.byId(String(id)))).data;
  }
  async create(body: CreateAuditPartnerRequest): Promise<ApiResponse<AuditPartner>> {
    return (await post<ApiResponse<AuditPartner>>(fsaAuditPartnerEndpoints.create(), body)).data;
  }
  async update(id: ID, body: UpdateAuditPartnerRequest): Promise<ApiResponse<AuditPartner>> {
    return (await put<ApiResponse<AuditPartner>>(fsaAuditPartnerEndpoints.update(String(id)), body)).data;
  }
  async remove(id: ID): Promise<void> {
    await del(fsaAuditPartnerEndpoints.remove(String(id)));
  }
}

class FsaDirectorSignatoryService {
  async getAll(params?: DirectorSignatorySearchRequest): Promise<PaginatedResponse<DirectorSignatory>> {
    const endpoint = params ? buildEndpointWithQuery(DIRECTORS_BASE, params as any) : fsaDirectorSignatoryEndpoints.list();
    return toPaged<DirectorSignatory>((await get<any>(endpoint)).data);
  }
  async getById(id: ID): Promise<ApiResponse<DirectorSignatory>> {
    return (await get<ApiResponse<DirectorSignatory>>(fsaDirectorSignatoryEndpoints.byId(String(id)))).data;
  }
  async create(body: CreateDirectorSignatoryRequest): Promise<ApiResponse<DirectorSignatory>> {
    return (await post<ApiResponse<DirectorSignatory>>(fsaDirectorSignatoryEndpoints.create(), body)).data;
  }
  async update(id: ID, body: UpdateDirectorSignatoryRequest): Promise<ApiResponse<DirectorSignatory>> {
    return (await put<ApiResponse<DirectorSignatory>>(fsaDirectorSignatoryEndpoints.update(String(id)), body)).data;
  }
  async remove(id: ID): Promise<void> {
    await del(fsaDirectorSignatoryEndpoints.remove(String(id)));
  }
}

class FsaEngagementSignaturePanelService {
  /** Get the panel for an engagement. Returns null when 404 (not yet configured). */
  async getByEngagement(engagementId: ID): Promise<EngagementSignaturePanel | null> {
    try {
      const resp = (await get<ApiResponse<EngagementSignaturePanel>>(
        fsaEngagementSignaturePanelEndpoints.byEngagement(String(engagementId))
      )).data;
      return resp.data ?? null;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  /** Get a carry-forward draft from the prior-year sibling's panel. Returns null when no prior is available. */
  async getCarryForwardDraft(engagementId: ID): Promise<UpsertEngagementSignaturePanelRequest | null> {
    try {
      const resp = (await get<ApiResponse<UpsertEngagementSignaturePanelRequest>>(
        fsaEngagementSignaturePanelEndpoints.carryForwardDraft(String(engagementId))
      )).data;
      return resp.data ?? null;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  async upsert(body: UpsertEngagementSignaturePanelRequest): Promise<ApiResponse<EngagementSignaturePanel>> {
    return (await post<ApiResponse<EngagementSignaturePanel>>(fsaEngagementSignaturePanelEndpoints.upsert(), body)).data;
  }

  async remove(id: ID): Promise<void> {
    await del(fsaEngagementSignaturePanelEndpoints.remove(String(id)));
  }
}

export const fsaAuditFirmService = new FsaAuditFirmService();
export const fsaAuditPartnerService = new FsaAuditPartnerService();
export const fsaDirectorSignatoryService = new FsaDirectorSignatoryService();
export const fsaEngagementSignaturePanelService = new FsaEngagementSignaturePanelService();
